import { isDraftPreviewEnabled } from './draftPreview';
import { MainNavProps } from 'components/MainNav/types';
import { HeaderCtaProps } from 'components/HeaderCTA/types';
import { FooterProps } from 'components/Footer/types';
import { FeaturedBannerProps } from 'components/FeaturedBanner/types';
import { HeaderLogoProps } from 'components/HeaderLogo/types';
import { DisclosureGroupingsProps } from 'components/DisclosureGroupings/types';
import { DisclosureBannerProps } from 'components/DisclosureBanner/types';
import { HeroConsolidatedProps } from 'components/HeroConsolidated/types';
import { HomepageHeroProps } from 'components/HomepageHero/types';
import { ModuleTypeName, ModuleTypes } from './modules';
import { NinetailedContentfulModule } from 'libs/contentful/queries/ninetailed';
import { getContentfulClient } from './experiences';
import { adaptEntry } from 'libs/contentful/rest/adapter/transforms';
import { buildIncludesMap } from 'libs/contentful/rest/adapter/type-guards';

export const SECTION_MODULE_TYPES = [
  'SectionHero',
  'SectionNavigation',
  'SectionFooter',
  'SectionDisclosures',
  'PageSection',
  'SectionBanner',
] as const;

export type SectionModuleType = (typeof SECTION_MODULE_TYPES)[number];

type SectionModuleConfig = {
  [K in SectionModuleType]: {
    fetchFunction: (id: string) => Promise<Record<string, unknown> | null>;
    dataProperty: string;
    variantDataPath?: string; // Path to extract variant data from for disclosure extraction
    variantExtractNestedModule?: boolean; // If true, extract from nested module at variantDataPath; if false, pass entire variant with __typename
  };
};

export const SECTION_MODULE_CONFIG: SectionModuleConfig = {
  SectionHero: {
    fetchFunction: getSectionHeroData,
    dataProperty: 'hero',
    variantDataPath: 'hero', // For variants, extract disclosures from the 'hero' property
    variantExtractNestedModule: true, // The 'hero' property is a complete module (e.g., HeroConsolidated)
  },
  SectionDisclosures: {
    fetchFunction: getSectionDisclosuresData,
    dataProperty: 'disclosuresCollection',
  },
  SectionFooter: {
    fetchFunction: getSectionFooterData,
    dataProperty: 'footer',
  },
  SectionNavigation: {
    fetchFunction: getSectionNavigationData,
    dataProperty: 'nav',
  },
  PageSection: {
    fetchFunction: async () => null, // PageSection doesn't need separate fetching
    dataProperty: 'modulesCollection',
    variantDataPath: 'modulesCollection', // For variants, extract disclosures from the 'modulesCollection' property
    variantExtractNestedModule: false, // The 'modulesCollection' is a property, not a complete module
  },
  SectionBanner: {
    fetchFunction: getSectionBannerData,
    dataProperty: 'headerBannerCollection',
  },
};

export const isSectionModule = (moduleName: string): moduleName is SectionModuleType => {
  return Object.keys(SECTION_MODULE_CONFIG).includes(moduleName);
};

/**
 * Generic function to process section modules and fetch their detailed data.
 * Avoids query complexity by fetching section data separately.
 */
export const processSectionModule = async (
  module: ModuleTypes[ModuleTypeName],
  sectionType: SectionModuleType,
  fetchFunction: (id: string) => Promise<Record<string, unknown> | null>,
  dataProperty: string,
) => {
  if (module?.__typename === sectionType && module.sys?.id) {
    try {
      const sectionDetails = await fetchFunction(module.sys.id);
      if (sectionDetails?.[dataProperty]) {
        // Merge the detailed data into the module
        (module as unknown as Record<string, unknown>)[dataProperty] = sectionDetails[dataProperty];
      } else {
        console.warn(`processSectionModule: ${dataProperty} is missing in sectionDetails for ${sectionType}`);
      }
    } catch (error) {
      console.error(`Failed to fetch ${sectionType} details:`, error);
    }
  }
};

/**
 * Generic function to process all section modules in a collection
 */
export const processAllSectionModules = async (modules: ModuleTypes[ModuleTypeName][]) => {
  const promises = modules.flatMap(module =>
    Object.entries(SECTION_MODULE_CONFIG).map(([sectionType, config]) =>
      processSectionModule(module, sectionType as SectionModuleType, config.fetchFunction, config.dataProperty),
    ),
  );

  return Promise.all(promises);
};

/**
 * Generic function to process section module experiences for Ninetailed
 */
type NinetailedComponent = {
  type: string;
  variants?: NinetailedContentfulModule[];
  [key: string]: unknown;
};

type IncompleteModule = {
  __typename: string;
  sys: { id: string };
};

type ModuleCollection<T = IncompleteModule> = {
  items: T[];
};

type SectionModuleData = IncompleteModule | ModuleCollection<IncompleteModule>;

const isIncompleteModule = (module: SectionModuleData) => {
  if (!module || typeof module !== 'object') {
    return false;
  }

  // Check if this is a collection with items (like disclosuresCollection)
  if ('items' in module) {
    const items = module.items;

    if (!Array.isArray(items) || items.length === 0) {
      return false;
    }

    // Check if the first item in the collection is incomplete
    const firstItem = items[0];
    return hasIncompleteFields(firstItem);
  }

  // Handle the standard case (single module)
  return hasIncompleteFields(module);
};

const hasIncompleteFields = (item: unknown) => {
  if (!item || typeof item !== 'object') {
    return false;
  }

  const obj = item as Record<string, unknown>;
  const keys = Object.keys(obj);

  // Check for required fields
  const hasTypename = typeof obj.__typename === 'string';
  const hasSysId = obj.sys !== null && typeof obj.sys === 'object' && 'id' in obj.sys;

  // Incomplete modules should only have __typename and sys.id
  return Boolean(hasTypename && hasSysId && keys.length <= 2);
};

export const processSectionModuleExperiences = async <T extends Record<string, unknown>>(
  mappedExperiences: T[],
  sectionType: SectionModuleType,
  fetchFunction: (id: string) => Promise<Record<string, unknown> | null>,
  dataProperty: string,
) => {
  return Promise.all(
    mappedExperiences.map(async experience => {
      if (!experience?.components) return experience;

      const mergedComponents = await Promise.all(
        (experience.components as NinetailedComponent[]).map(async component => {
          if (!component.variants) return component;

          const mergedVariants = await Promise.all(
            component.variants.map(async variant => {
              if (!(dataProperty in variant && variant.id)) return variant;

              const contentfulVariant = variant as NinetailedContentfulModule & Record<string, unknown>;
              const dataPropertyValue = contentfulVariant[dataProperty] as IncompleteModule;

              if (isIncompleteModule(dataPropertyValue)) {
                try {
                  const sectionData = await fetchFunction(variant.id);
                  if (sectionData?.[dataProperty]) {
                    contentfulVariant[dataProperty] = sectionData[dataProperty];
                  }
                } catch (error) {
                  console.error(`Failed to fetch ${sectionType} details for variant:`, error);
                }
              }
              return variant;
            }),
          );

          return { ...component, variants: mergedVariants };
        }),
      );

      return { ...experience, components: mergedComponents };
    }),
  );
};

/**
 * Fetches a section entry via REST and adapts it into the shape downstream
 * consumers expect. With `include: 10` link resolution, all nested modules and
 * assets come back inline - no additional fetches needed.
 */
async function fetchAdaptedSectionEntry(entryId: string): Promise<Record<string, unknown> | null> {
  const draftPreviewEnabled = await isDraftPreviewEnabled();
  const client = getContentfulClient({ draftPreviewEnabled });

  const response = await client.getEntries({
    'sys.id': entryId,
    include: 10,
    limit: 1,
  });
  const entry = response.items[0];
  if (!entry) return null;

  const includes = {
    Entry: response.includes?.Entry ?? [],
    Asset: response.includes?.Asset ?? [],
  };
  const includesMap = buildIncludesMap(includes);
  if (entry.sys?.id) {
    includesMap.entries.set(entry.sys.id, entry);
  }

  const adapted = adaptEntry(entry, includesMap, new WeakSet<object>(), true);
  if (!adapted || typeof adapted !== 'object' || Array.isArray(adapted)) return null;
  return adapted as Record<string, unknown>;
}

/**
 * Fetches full SectionHero data for a given SectionHero ID.
 */
type SectionHeroResponse = {
  __typename: string;
  hero: HeroConsolidatedProps | HomepageHeroProps;
};

export async function getSectionHeroData(sectionHeroId: string): Promise<SectionHeroResponse | null> {
  const adapted = await fetchAdaptedSectionEntry(sectionHeroId);
  return adapted as SectionHeroResponse | null;
}

/**
 * Fetches full SectionNavigation data for a given SectionNavigation ID.
 */
type SectionNavigationResponse = {
  __typename: string;
  nav: MainNavProps | HeaderCtaProps;
};

export async function getSectionNavigationData(sectionNavigationId: string): Promise<SectionNavigationResponse | null> {
  const adapted = await fetchAdaptedSectionEntry(sectionNavigationId);
  return adapted as SectionNavigationResponse | null;
}

/**
 * Fetches full SectionFooter data for a given SectionFooter ID.
 */
type SectionFooterResponse = {
  __typename: string;
  footer: FooterProps;
};

export async function getSectionFooterData(sectionFooterId: string): Promise<SectionFooterResponse | null> {
  const adapted = await fetchAdaptedSectionEntry(sectionFooterId);
  return adapted as SectionFooterResponse | null;
}

/**
 * Fetches full SectionDisclosures data for a given SectionDisclosures ID.
 */
type SectionDisclosuresResponse = {
  __typename: string;
  disclosuresCollection: {
    items: (DisclosureGroupingsProps | DisclosureBannerProps)[];
  };
};

export async function getSectionDisclosuresData(
  sectionDisclosuresId: string,
): Promise<SectionDisclosuresResponse | null> {
  const adapted = await fetchAdaptedSectionEntry(sectionDisclosuresId);
  return adapted as SectionDisclosuresResponse | null;
}

/**
 * Fetches full SectionBanner data for a given SectionBanner ID.
 */
type SectionBannerResponse = {
  __typename: string;
  headerBannerCollection: {
    items: (FeaturedBannerProps | HeaderLogoProps)[];
  };
};

export async function getSectionBannerData(sectionBannerId: string): Promise<SectionBannerResponse | null> {
  const adapted = await fetchAdaptedSectionEntry(sectionBannerId);
  return adapted as SectionBannerResponse | null;
}
